import {
  baseCamera2DData,
  baseCamera3DData,
  baseLayers,
  emptyClientInfo,
  lastView,
} from '@baseValues'
import { MorphDictionary } from '@components/Sidebar/Hole/HolePropertyInput'
import { getGLBData } from '@proxies/getGLBData'
import { getTexture } from '@proxies/getTexture'
import { openProyect } from '@proxies/openProyect'
import { setTextos, t } from '@proxies/translation'
import { setResource } from '@store'
import { popHistorySuppress, pushHistorySuppress, getHistoryBatchDepth, isHistoryRecordingSuppressed } from './history'
import { isHistoryPaused } from './history/historyEngine'
import {
  AnyID,
  BackOpenProject,
  DesignerInfoPayload,
  Getter,
  Hole,
  Item,
  LinkedItem,
  Modes,
  Room,
  Setter,
  Store,
  View,
  Walls,
  Zone,
} from '@types'
import { calculateDeltasBetweenSelectedItems } from '@utils/calcDeltaItems'
import { captureLiveCameraView } from '@utils/camera/cameraControlsRegistry'
import { isValidCameraView, normalizeCameraView } from '@utils/camera/cameraView'
import getStandardMaterial from '@utils/conversion/getStandardMaterial'
import { resolveApiTextureColor } from '@utils/sceneObjects/resolvePbrMaterial'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import { radians } from '@utils/generalMaths/geometry'
import isHole from '@utils/sceneObjects/assertions/isHole'
import isItem from '@utils/sceneObjects/assertions/isItem'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import isWall from '@utils/sceneObjects/assertions/isWall'
import getId from '@utils/sceneObjects/getId'
import { setGLTFLoader } from '@utils/sceneObjects/setGLTFLoader'
import { getWallCOBB } from '@utils/sceneObjects/walls/getWallCOBB'
import { publishPersistedProjectCollisions } from '@utils/geometryIntegrity/debug'
import { runAsPersistedGeometryImport } from '@utils/geometryIntegrity/commitGuard'
import { pushGeometryOperation } from '@utils/geometryIntegrity/operation'
import SuperJSON from 'superjson'
import { Euler, Matrix3, Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'

export type ProjectActions = ReturnType<typeof createProjectActions>
// Este proyecto no usa texturas universales (suelo/pared/techo/tabiques).
// Bloqueamos estas llamadas para evitar tráfico innecesario.
const DISABLE_UNIVERSAL_TEXTURE_REQUESTS = true

export const createProjectActions = (set: Setter, get: Getter) => ({
  setMode: (mode: Modes) =>
    set((store) => {
      store.mode = mode
    }),
  setSelectedRoom: (room: Room) =>
    set((store) => {
      if (!room) return console.warn('Room not provided')
      store.selectedRoom = room
    }),

  setPreference: <T extends keyof Store['prefs']>(name: T, value: Store['prefs'][T]) => {
    set((store) => {
      const prefs = store.prefs
      prefs[name] = value
    })
  },
  editPrice: (price: number) => {
    set((store) => {
      store.price += price
    })
  },
  calculatePrice: () => {
    set((store) => {
      const items = store.layers[store.currentLayer].items
      const newPrice = Array.from(items).reduce((acc, el) => {
        const item = store.items.get(el) as { price: number }

        if (!item) return acc
        const itemPrice = item?.price
        if (!itemPrice) return acc
        return (acc += itemPrice)
      }, 0)
      store.price = newPrice
    })
  },
  setLanguageOptions: (options: Store['textos']['idioma'][]) => {
    //TODO: check what the back gives us
    set((store) => {
      store.languageOptions = options
    })
  },
  setCameraMove: (newState?: boolean) => {
    set((store) => {
      store.shouldCameraMove = newState !== undefined ? newState : !store.shouldCameraMove
    })
  },
  setCanDraw: (bool: boolean) => {
    set((store) => {
      store.canDraw = bool
    })
  },
  t: (text: keyof Store['textos']['textos']) => t(set, get, text),
  setTextos: (textos: Store['textos']) => {
    setTextos(get, set, textos)
  },
  setIdiomaCultura: (id: string) => {
    set((store) => {
      store.idiomaCultura = id
    })
  },
  setClientInfo: (clientInfo: Store['clientInfo']) => {
    set((store) => {
      store.clientInfo = {
        ...clientInfo,
      }
    })
  },
  setPendingDesignerInfo: (payload: DesignerInfoPayload | null) => {
    set((store) => {
      store.pendingDesignerInfo = payload
    })
  },
  setPrefs: (prefs: Store['prefs'], prefsInfo: Store['prefsInfo']) => {
    set((store) => {
      store.prefsInfo = prefsInfo
      store.prefs = prefs
      setResource('prefs', true)
    })
  },
  setTextures: async <S extends string, N extends number>(
    catalogo: S,
    unidadsuelo: N,
    unidadpared0: N,
    unidadpared1: N,
    unidadtecho: N,
    unidadtabique0: N,
    unidadtabique1: N,
    unidadtabique2: N,
    unidadtabique3: N,
    unidadtabique4: N,
    unidadtabique5: N,
    token?: string,
  ) => {
    if (DISABLE_UNIVERSAL_TEXTURE_REQUESTS) {
      setResource('texturas', true)
      return
    }
    // prefs?.CATALOGOUNIVERSAL &&
    //       prefs?.UNIDADSUELO &&
    //       prefs?.UNIDADPARED0 &&
    //       prefs?.UNIDADPARED1 &&
    //       prefs?.UNIDADTECHO
    const catalog = get().prefs['CATALOGOUNIVERSAL'] ?? catalogo
    const isLogged = window.localStorage.getItem('isUserLogged')
    const texture1 = await getTexture(catalog, unidadsuelo, token)
    const texture2 = await getTexture(catalog, unidadpared0, token)
    const texture3 = await getTexture(catalog, unidadpared1, token)
    const texture4 = await getTexture(catalog, unidadtecho, token)
    const texture5 = await getTexture(catalog, unidadtabique0, token)
    const texture6 = await getTexture(catalog, unidadtabique1, token)
    const texture7 = await getTexture(catalog, unidadtabique2, token)
    const texture8 = await getTexture(catalog, unidadtabique3, token)
    const texture9 = await getTexture(catalog, unidadtabique4, token)
    const texture10 = await getTexture(catalog, unidadtabique5, token)

    if (
      !texture1 ||
      !texture2 ||
      !texture3 ||
      !texture4 ||
      !texture5 ||
      !texture6 ||
      !texture7 ||
      !texture8 ||
      !texture9 ||
      !texture10
    )
      return

    texture2.name = 'Frontal'
    texture2.id = 'textureA'
    texture3.name = 'Trasera'
    texture3.id = 'textureB'
    texture1.id = 'areaSuelo'
    texture1.name = 'Area'
    texture4.id = 'areaTecho'
    texture4.name = 'Mover objeto'
    texture5.id = 'tabiqueCara0'
    texture5.name = 'Mover objeto'
    texture6.id = 'tabiqueCara1'
    texture6.name = 'Mover objeto'
    texture7.id = 'tabiqueCara2'
    texture7.name = 'Mover objeto'
    texture8.id = 'tabiqueCara3'
    texture8.name = 'Mover objeto'
    texture9.id = 'tabiqueCara4'
    texture9.name = 'Mover objeto'
    texture10.id = 'tabiqueCara5'
    texture10.name = 'Mover objeto'

    const items = new Map()
    const areas = new Map()

    set((store) => {
      store.texture1 = texture1
      store.texture2 = texture2
      store.texture3 = texture3
      store.texture4 = texture4
      store.texture5 = texture5
      store.texture6 = texture6
      store.texture7 = texture7
      store.texture8 = texture8
      store.texture9 = texture9
      store.texture10 = texture10
      if (!isLogged) {
        store.items = items
        store.areas = areas
      }
      setResource('texturas', true)
    })
  },
  resetTextures: async <S extends string, N extends number>(
    catalogo: S,
    unidadsuelo: N,
    unidadpared0: N,
    unidadpared1: N,
    unidadtecho: N,
    unidadtabique0: N,
    unidadtabique1: N,
    unidadtabique2: N,
    unidadtabique3: N,
    unidadtabique4: N,
    unidadtabique5: N,
  ) => {
    if (DISABLE_UNIVERSAL_TEXTURE_REQUESTS) {
      setResource('texturas', true)
      return
    }
    const texture1 = await getTexture(catalogo, unidadsuelo)
    const texture2 = await getTexture(catalogo, unidadpared0)
    const texture3 = await getTexture(catalogo, unidadpared1)
    const texture4 = await getTexture(catalogo, unidadtecho)
    const texture5 = await getTexture(catalogo, unidadtabique0)
    const texture6 = await getTexture(catalogo, unidadtabique1)
    const texture7 = await getTexture(catalogo, unidadtabique2)
    const texture8 = await getTexture(catalogo, unidadtabique3)
    const texture9 = await getTexture(catalogo, unidadtabique4)
    const texture10 = await getTexture(catalogo, unidadtabique5)
    if (!texture1 || !texture2 || !texture3 || !texture4) return

    texture2.name = 'Frontal'
    texture2.id = 'textureA'
    texture3.name = 'Trasera'
    texture3.id = 'textureB'
    texture1.id = 'areaSuelo'
    texture1.name = 'Area'
    texture4.id = 'areaTecho'
    texture4.name = 'Mover objeto'
    texture5.id = 'tabiqueCara0'
    texture5.name = 'Mover objeto'
    texture6.id = 'tabiqueCara1'
    texture6.name = 'Mover objeto'
    texture7.id = 'tabiqueCara2'
    texture7.name = 'Mover objeto'
    texture8.id = 'tabiqueCara3'
    texture8.name = 'Mover objeto'
    texture9.id = 'tabiqueCara4'
    texture9.name = 'Mover objeto'
    texture10.id = 'tabiqueCara5'
    texture10.name = 'Mover objeto'

    set((store) => {
      store.texture1 = getStandardMaterial(texture1)
      store.texture2 = getStandardMaterial(texture2)
      store.texture3 = getStandardMaterial(texture3)
      store.texture4 = getStandardMaterial(texture4)
      store.texture5 = getStandardMaterial(texture5)
      store.texture6 = getStandardMaterial(texture6)
      store.texture7 = getStandardMaterial(texture7)
      store.texture8 = getStandardMaterial(texture8)
      store.texture9 = getStandardMaterial(texture9)
      store.texture10 = getStandardMaterial(texture10)
      setResource('texturas', true)
    })
  },

  setCamera3D: (data: Store['camera3DData']) => {
    set((store) => {
      store.camera3DData = data
    })
  },
  setCamera2DPosition: (position: Store['camera2DPosition']) => {
    set((store) => {
      store.camera2DPosition = position
    })
  },
  requestCameraFitToItems: () => {
    set((store) => {
      if (store.cameraViewPreservationDepth > 0 || store.unitReplaceInFlight) return
      store.shouldFitCameraToItems = true
    })
  },
  clearCameraFitToItems: () => {
    set((store) => {
      store.shouldFitCameraToItems = false
    })
  },
  beginCameraViewPreservation: () => {
    set((store) => {
      if (store.cameraViewPreservationDepth === 0) {
        const liveView = captureLiveCameraView()
        const normalized =
          normalizeCameraView(liveView) ?? normalizeCameraView(store.lastView)
        if (normalized && isValidCameraView(normalized)) {
          store.lockedCameraView = {
            position: normalized.position.clone(),
            target: normalized.target.clone(),
          }
          store.lastView = {
            position: normalized.position.clone(),
            target: normalized.target.clone(),
          }
        } else {
          store.lockedCameraView = null
        }
      }
      store.cameraViewPreservationDepth += 1
      store.shouldFitCameraToItems = false
    })
  },
  endCameraViewPreservation: () => {
    set((store) => {
      store.cameraViewPreservationDepth = Math.max(0, store.cameraViewPreservationDepth - 1)
      if (store.cameraViewPreservationDepth === 0 && store.lockedCameraView) {
        store.lastView = {
          position: store.lockedCameraView.position.clone(),
          target: store.lockedCameraView.target.clone(),
        }
        store.cameraViewRestoreNonce += 1
        store.lockedCameraView = null
      }
      store.shouldFitCameraToItems = false
    })
  },
  setUnitReplaceInFlight: (value: boolean) => {
    set((store) => {
      store.unitReplaceInFlight = value
    })
  },
  setLastView: (lastView: Store['lastView']) => {
    set((store) => {
      store.lastView = lastView
    })
  },
  addSavedView: (newView: View) => {
    set((store) => ({
      savedViews: [...(store.savedViews || []), newView],
    }))
  },
  deleteSavedView: (index: number) => {
    set((store) => ({
      savedViews: store.savedViews.filter((_, i) => i !== index),
    }))
  },
  setSavedViews: (views: View[]) => {
    set(() => ({
      savedViews: views,
    }))
  },
  setStoreFromBack: async (
    id: number,
    tmp = false,
    comesFromStances = false,
    projectInfo?: DesignerInfoPayload | null,
  ) => {
    const fallbackDesignerInfo = get().pendingDesignerInfo ?? null
    const designerInfo = projectInfo ?? fallbackDesignerInfo ?? null

    const backResponse = await openProyect(id, tmp)
    const info = SuperJSON.parse(backResponse.json) as BackOpenProject
    const loader = setGLTFLoader()
    const fetchedGLB = new Set()

    set((store) => {
      store.sceneGLB = new Map()
      store.sceneBoundingBoxes = new Map()
    })

    for (const itemData of info.items.values()) {
      const toSearch = itemData.catalogID || itemData.idUnidades
      if (fetchedGLB.has(toSearch)) continue
      fetchedGLB.add(toSearch)

      if (itemData.glbPath && toSearch) {
        const glb = await loader.loadAsync(itemData.glbPath)
        const glbData = await getGLBData(toSearch)

        set((store) => {
          if (store.sceneGLB.has(toSearch)) return
          if (itemData.type === 'holes') {
            store.sceneGLB.set(toSearch, {
              glb: glb,
              backGLBInfo: glbData,
            })
            return
          }
          store.sceneGLB.set(toSearch, {
            glb: glb,
            backGLBInfo: glbData,
          })
        })
      }
    }
    const items = new Map()
    info.items?.forEach((item) => {
      // TODO: partitions
      if (item.type === 'walls') {
        if (item.perpendicularPoint) {
          const { x, y, z } = item.perpendicularPoint
          const pp = toVector3([x, y, z])
          const { x: _x, y: _y, z: _z } = item.symmetricalPerpendicularPoint
          const spp = toVector3([_x, _y, _z])
          items.set(item.id, {
            ...item,
            perpendicularPoint: pp,
            symmetricalPerpendicularPoint: spp,
          })
          return
        }
      }
      if (item?.zones) {
        const itemCopy = { ...item }
        for (const zoneName in itemCopy.zones) {
          const zone = { ...itemCopy.zones[zoneName] }
          // Check if the zone has a valid position and rotation
          if (zone.position && zone.rotation) {
            const fixedPosition = toVector3([zone.position.x, zone.position.y, zone.position.z])
            const fixedRotation = new Euler(
              zone.rotation._x,
              zone.rotation._y,
              zone.rotation._z,
              zone.rotation._order,
            )
            const fixedScale = toVector3([zone.scale.x, zone.scale.y, zone.scale.z])
            zone.scale = fixedScale
            zone.position = fixedPosition
            zone.rotation = fixedRotation
          }
          // Handle other textures of the zone if needed
          // For example, you can access zone.scale or other textures here
        }
        items.set(item.id, itemCopy)
      } else {
        items.set(item.id, item)
      }
      if (item?.matrix) {
        const itemCopy = { ...item }
        const fixedMatrix = new Matrix4().fromArray(item.matrix.elements)
        itemCopy.matrix = fixedMatrix
        items.set(item.id, itemCopy)
      } else {
        items.set(item.id, item)
      }
    })
    const sceneBoundingBoxes = new Map()
    for (const [key, value] of info.sceneBoundingBoxes.entries()) {
      const item = items.get(key)
      // if (!item) continue
      const type = value.type
      const { x, y, z } = value.boundingBox.center
      const { x: sizeX, y: sizeY, z: sizeZ } = value.boundingBox.halfSize
      if (key.startsWith('COBB')) {
        const finalKey = key.replace('COBB_', '')
        const actualItem = items.get(finalKey)
        if (!actualItem) continue
        const rotation = actualItem.rotation
        const boundingBox = new OBB(toVector3([x, y, z]), toVector3([sizeX, sizeY, sizeZ]))
        const collisionOBB = new OBB(
          toVector3([x, 1, z]),
          toVector3([0, actualItem.height, actualItem.thickness / 2]),
        )
        const mat4 = new Matrix4().makeRotationFromEuler(
          new Euler(0, -rotation + 2 * Math.PI, 0, 'XYZ'),
        )
        const rot = new Matrix3().setFromMatrix4(mat4)
        boundingBox.rotation = rot
        sceneBoundingBoxes.set(key, { type, boundingBox: collisionOBB })
        continue
      }
      if (!item) continue
      const boundingBox = new OBB(toVector3([x, y, z]), toVector3([sizeX, sizeY, sizeZ]))
      sceneBoundingBoxes.set(key, { type, boundingBox })
    }
    const popProjectLoad = pushGeometryOperation('project-load')
    try {
    runAsPersistedGeometryImport(() => {
    set((store) => {
      store.items = items
      store.areas = info.areas
      store.layers = info.layers
      store.price = info.price ?? 0
      store.storeHistory = []
      store.layers[store.currentLayer].selected.clear()
      store.canDraw = true
      store.currentArea = ''
      store.currentFirstWall = ''
      store.currentLayer = info.currLayer
      store.currentObjectID = ''
      store.mode = 'MODE_IDLE'
      store.sceneBoundingBoxes = sceneBoundingBoxes
      if (!comesFromStances) {
        const disenadorActual =
          designerInfo?.disenadorActual ?? backResponse.disenyo.disenadorActual ?? null
        const disenadorOriginal =
          designerInfo?.disenadorOriginal ?? backResponse.disenyo.disenadorOriginal ?? null
        const numProyectoTpc =
          designerInfo?.numProyectoTpc ?? backResponse.disenyo.numProyectoTpc ?? null

        store.clientInfo = {
          nombreProyecto: backResponse.disenyo.nombreProyecto,
          descripcion: backResponse.disenyo.descripcion,
          nombre: backResponse.disenyo.nombre,
          apellidos: backResponse.disenyo.apellidos,
          direccion: backResponse.disenyo.direccion,
          codPostal: backResponse.disenyo.codPostal,
          poblacion: backResponse.disenyo.poblacion,
          email: backResponse.disenyo.email,
          pais: backResponse.disenyo.pais,
          movil: backResponse.disenyo.movil?.toString?.() ?? '',
          numProyectoTpc: numProyectoTpc ?? undefined,
          idDisenos: backResponse.disenyo.idDisenos,
          idVersiones: backResponse.disenyo.idVersiones,
          idImpuestos: backResponse.disenyo.idImpuestos ?? 0,
          disenadorActual,
          disenadorOriginal,
        }
      } else {
        store.clientInfo = {
          ...emptyClientInfo,
          nombreProyecto: backResponse.disenyo.nombreProyecto,
          numProyectoTpc: backResponse.disenyo.numProyectoTpc ?? undefined,
          disenadorActual: backResponse.disenyo.disenadorActual ?? null,
          disenadorOriginal: backResponse.disenyo.disenadorOriginal ?? null,
          movil: backResponse.disenyo.movil?.toString?.() ?? '',
        }
      }
      store.collisionableItems = info.collisionableItems ?? new Set()

      // Asegurar que todos los items universales estén en collisionableItems
      store.items.forEach((item, itemId) => {
        if (item.type === 'items' && (item as any).universal === true) {
          store.collisionableItems.add(itemId)
        }
      })
      store.savedViews = info.savedViews || []
      const restoredLastView = normalizeCameraView(info.lastView)
      store.lastView =
        restoredLastView && isValidCameraView(restoredLastView)
          ? restoredLastView
          : {
              position: lastView.position.clone(),
              target: lastView.target.clone(),
            }
      if (restoredLastView && isValidCameraView(restoredLastView)) {
        store.camera3DData = {
          ...store.camera3DData,
          position: restoredLastView.position.clone(),
        }
      }
      store.esMio = backResponse.esMio ?? true
      store.pendingDesignerInfo = null

      // Asegurar que las luces base existan siempre (especialmente cuando no hay paredes)
      const defaultHeight = 4000
      if (
        !store.items.has('lights_baseSceneLight') ||
        !store.layers[store.currentLayer].items.has('lights_baseSceneLight')
      ) {
        get()
          .drawLight('lights_baseSceneLight', '', 'LUZGENERAL', false, defaultHeight)
          .catch(() => {
            // Error silencioso
          })
      }
      if (
        !store.items.has('lights_baseAreaLight') ||
        !store.layers[store.currentLayer].items.has('lights_baseAreaLight')
      ) {
        get()
          .drawLight('lights_baseAreaLight', '', 'Luzgeneral2', false, defaultHeight)
          .catch(() => {
            // Error silencioso
          })
      }

      // Seleccionar automáticamente el primer item visible para mostrar el gizmo
      const allItems = Array.from(store.items.values())
      const firstVisibleItem = allItems.find(
        (item) => (isItem(item) || isLinkedItem(item)) && item.visible !== false,
      )
      if (firstVisibleItem) {
        store.layers[store.currentLayer].selected.add(firstVisibleItem.id)
        store.currentObjectID = firstVisibleItem.id
      }
    })
    })
    publishPersistedProjectCollisions(get().items as Map<string, any>)
    } finally {
      popProjectLoad()
    }
    get().clearHistory()
  },
  addProjectItemsToCurrentScene: async (idVersiones: number) => {
    try {
      console.log('🔍 Intentando abrir proyecto:', idVersiones)
      // Usar tmp = true como en estancias
      const backResponse = await openProyect(idVersiones, true)
      console.log('📦 Respuesta recibida:', backResponse)

      // Validar que backResponse existe y tiene la propiedad json
      if (!backResponse || !backResponse.json) {
        console.error(
          '❌ No se pudo obtener la respuesta del proyecto:',
          idVersiones,
          'Respuesta:',
          backResponse,
        )
        return false
      }

      const info = SuperJSON.parse(backResponse.json) as BackOpenProject

      // Validar que info tiene items
      if (!info || !info.items || info.items.size === 0) {
        console.error('❌ El proyecto no tiene items o está vacío:', idVersiones)
        return false
      }

      const loader = setGLTFLoader()
      const fetchedGLB = new Set()
      const currentStore = get()

      // Cargar GLBs necesarios sin limpiar los existentes
      for (const itemData of info.items.values()) {
        const toSearch = itemData.catalogID || itemData.idUnidades
        if (fetchedGLB.has(toSearch)) continue
        fetchedGLB.add(toSearch)

        // Solo cargar si no está ya en la escena
        if (!currentStore.sceneGLB.has(toSearch) && itemData.glbPath && toSearch) {
          const glb = await loader.loadAsync(itemData.glbPath)
          const glbData = await getGLBData(toSearch)

          set((store) => {
            if (store.sceneGLB.has(toSearch)) return
            if (itemData.type === 'holes') {
              store.sceneGLB.set(toSearch, {
                glb: glb,
                backGLBInfo: glbData,
              })
              return
            }
            store.sceneGLB.set(toSearch, {
              glb: glb,
              backGLBInfo: glbData,
            })
          })
        }
      }

      // Procesar items del proyecto y agregarlos al proyecto actual
      const newItems = new Map()
      const idMapping = new Map<string, string>() // Mapeo de IDs antiguos a nuevos
      const currentIncrement = get().increment
      let newIncrement = currentIncrement

      // Primero, crear mapeo de IDs y procesar todos los items
      info.items?.forEach((item) => {
        // Generar nuevos IDs para evitar conflictos
        let newId: string
        if (item.type === 'walls') {
          newId = getId('walls')
        } else if (item.type === 'holes') {
          newId = getId('holes')
        } else if (item.type === 'linkedItem') {
          newId = getId('linkedItem')
        } else if (item.type === 'linkedSon') {
          newId = getId('linkedSon')
        } else if (item.type === 'items') {
          newId = getId('items')
        } else if (item.type === 'lights') {
          newId = getId('lights')
        } else {
          newId = getId('items')
        }

        idMapping.set(item.id, newId)
      })

      // Ahora procesar items con los nuevos IDs
      info.items?.forEach((item) => {
        const newId = idMapping.get(item.id)!
        if (!newId) return

        // Procesar item manteniendo su estructura
        let processedItem: any = { ...item, id: newId }

        // Procesar walls
        if (item.type === 'walls') {
          if (item.perpendicularPoint) {
            const { x, y, z } = item.perpendicularPoint
            const pp = toVector3([x, y, z])
            const { x: _x, y: _y, z: _z } = item.symmetricalPerpendicularPoint
            const spp = toVector3([_x, _y, _z])
            processedItem = {
              ...processedItem,
              perpendicularPoint: pp,
              symmetricalPerpendicularPoint: spp,
            }
          }
        }

        // Procesar zones
        if (item?.zones) {
          const itemCopy = { ...processedItem }
          for (const zoneName in itemCopy.zones) {
            const zone = { ...itemCopy.zones[zoneName] }
            if (zone.position && zone.rotation) {
              const fixedPosition = toVector3([zone.position.x, zone.position.y, zone.position.z])
              const fixedRotation = new Euler(
                zone.rotation._x,
                zone.rotation._y,
                zone.rotation._z,
                zone.rotation._order,
              )
              const fixedScale = toVector3([zone.scale.x, zone.scale.y, zone.scale.z])
              zone.scale = fixedScale
              zone.position = fixedPosition
              zone.rotation = fixedRotation
            }
          }
          processedItem = itemCopy
        }

        // Procesar matrix
        if (item?.matrix) {
          const fixedMatrix = new Matrix4().fromArray(item.matrix.elements)
          processedItem.matrix = fixedMatrix
        }

        // Actualizar childs para linkedItems si existen
        if (item.type === 'linkedItem' && item.childs) {
          const newChilds = new Set<string>()
          item.childs.forEach((childId: string) => {
            const newChildId = idMapping.get(childId)
            if (newChildId) {
              newChilds.add(newChildId)
            }
          })
          processedItem.childs = newChilds
        }

        // Actualizar firstParentID para linkedSon
        if (item.type === 'linkedSon' && item.firstParentID) {
          const newParentId = idMapping.get(item.firstParentID)
          if (newParentId) {
            processedItem.firstParentID = newParentId
          }
        }

        newItems.set(newId, processedItem)
        newIncrement++
      })

      // Agregar items a la escena actual
      set((store) => {
        // Agregar items al mapa existente
        newItems.forEach((item, id) => {
          store.items.set(id, item)
          // Agregar a la capa actual
          store.layers[store.currentLayer].items.add(id)
        })

        // Actualizar increment
        store.increment = newIncrement

        // Actualizar sceneBoundingBoxes para los nuevos items
        newItems.forEach((item, id) => {
          if (item.type === 'walls') {
            const wall = item as Walls
            const wallCOBB = getWallCOBB(wall)
            if (wallCOBB) {
              store.sceneBoundingBoxes.set(`COBB_${id}`, {
                type: 'walls',
                boundingBox: wallCOBB,
              })
            }
          } else if (item.type !== 'walls' && item.matrix) {
            // Calcular bounding box para items
            const morphs = (item as Item).morphs || []
            if (morphs.length >= 3) {
              const rotationMatrix4 = new Matrix4().makeRotationFromEuler(
                new Euler(0, item.rotation || 0, 0, 'XYZ'),
              )
              const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)
              store.sceneBoundingBoxes.set(id, {
                boundingBox: new OBB(
                  toVector3([-morphs[2].default / 2, morphs[1].default / 2, morphs[0].default / 2]),
                  toVector3([morphs[0].default / 2, morphs[1].default / 2, morphs[2].default / 2]),
                  rotationMatrix,
                ),
                type: item.type,
              })
            }
          }

          // Agregar a collisionableItems si corresponde
          const glbData = store.sceneGLB.get(item.catalogID || item.idUnidades)
          if (glbData?.backGLBInfo) {
            if ((glbData.backGLBInfo.colision ?? true) || glbData.backGLBInfo.universal) {
              store.collisionableItems.add(id)
            }
          }
        })

        // Actualizar precio sumando el precio del proyecto cargado
        store.price = (store.price || 0) + (info.price || 0)

        // Seleccionar todos los items agregados
        const addedItemIds = Array.from(newItems.keys())
        if (addedItemIds.length > 0) {
          // Deseleccionar todo primero
          store.layers[store.currentLayer].selected.clear()

          // Seleccionar todos los items agregados directamente
          addedItemIds.forEach((id) => {
            store.layers[store.currentLayer].selected.add(id)
          })

          // Establecer el primer item como currentObjectID
          store.currentObjectID = addedItemIds[0]
        }
      })

      // Seleccionar los items usando la función select después de que termine el set
      // Esto asegura que la selección múltiple funcione correctamente
      const addedItemIds = Array.from(newItems.keys())
      if (addedItemIds.length > 0) {
        const selectFn = get().select
        const setDeltasFn = get().setDeltas
        const currentItems = get().items

        // Limpiar selección primero
        get().unselectAll()

        // Seleccionar todos los items usando la función select
        addedItemIds.forEach((id, index) => {
          // Para el primer item, no usar Ctrl (limpia la selección anterior)
          // Para los siguientes, usar Ctrl=true para agregar a la selección
          if (index === 0) {
            selectFn(id, false)
          } else {
            selectFn(id, true)
          }
        })

        // Calcular y establecer los deltas inmediatamente después de seleccionar
        // Esto es necesario para que funcione en 3D cuando arrastras los items
        if (addedItemIds.length > 1) {
          const selectedItemsSet = new Set(addedItemIds)
          const deltas = calculateDeltasBetweenSelectedItems(selectedItemsSet, currentItems)
          setDeltasFn(deltas)
        }
      }

      // Forzar invalidación de React Three Fiber
      try {
        const r3f = typeof window !== 'undefined' ? (window as any).r3f : undefined
        if (r3f?.invalidate) {
          r3f.invalidate()
          requestAnimationFrame(() => {
            r3f.invalidate()
          })
        }
      } catch (r3fError) {
        // Ignorar errores de invalidación
      }

      return true
    } catch (error) {
      console.error('Error agregando items del proyecto a la escena actual:', error)
      return false
    }
  },
  resetStore: () => {
    const drawLight = (
      id: string,
      icon: string,
      catalogID: string,
      show: boolean,
      height?: number,
    ) => get().drawLight(id, icon, catalogID, show, height)
    set((store) => {
      store.items = new Map()
      store.areas = new Map()
      store.layers = baseLayers
      store.price = 0
      store.sceneBoundingBoxes = new Map()
      store.collisionableItems = new Set()
      store.canDraw = true
      store.currentObjectID = ''
      store.increment = 1
      store.canDraw = true
      store.isModalOpen = false
      store.isProjectOpened = false
      store.currentRoof = ''
      store.currentArea = ''
      store.currentLayer = 0
      store.sceneGLB = new Map()
      store.camera2DPosition = baseCamera2DData
      store.camera3DData = baseCamera3DData
      store.lastView = {
        position: lastView.position.clone(),
        target: lastView.target.clone(),
      }
      store.clientInfo = { ...emptyClientInfo, idImpuestos: store.impuestos[0].idImpuestos }
      store.savedViews = []
      store.frontalActive = false
      // Crear ambas luces base automáticamente a altura 4000
      const defaultHeight = 4000
      drawLight('lights_baseSceneLight', '', 'LUZGENERAL', false, defaultHeight).catch(() => {
        // Error silencioso
      })
      drawLight('lights_baseAreaLight', '', 'Luzgeneral2', false, defaultHeight).catch(() => {
        // Error silencioso
      })
    })
    get().clearHistory()
  },

  editMorph: (itemID: string, morphTarget: Item['morphs'][number]['name'], value: number) => {
    set((store: Store) => {
      const item = store.items.get(itemID) as Item

      if (!item) return
      const selectedItems = store.layers[store.currentLayer].selected
      selectedItems.forEach((selectedId: string) => {
        const item = store.items.get(selectedId) as Item | LinkedItem | Hole
        if (!item) return
        const morphs = item.morphs
        if (!morphs) return
        // if (morphTarget === 'Fondo' && item.tipoUnidNorm === 'V') return
        morphs.forEach((el) => {
          if (el.name === morphTarget) {
            if (item.type === 'holes') {
              el._default = value
              el.default = value > el.max ? el.max : value < el.min ? el.min : value
              if (el.name === 'Fondo' && (item as Hole).tipoUnidNorm === 'P') {
                ;(item as any)[MorphDictionary?.[el.name]] = value + (item as any).offset
              } else {
                ;(item as any)[MorphDictionary?.[el.name]] = value
              }
              return
            }
            if (!item?.hasMorphs) {
              el._default = value
              return
            }
            //Solo actualizar default, NO _default (que mantiene el valor por defecto original)
            el.default = value > el.max ? el.max : value < el.min ? el.min : value
            if (el.name === 'Fondo' && item.tipoUnidNorm === 'P') {
              item[MorphDictionary?.[el.name]] = value + item.offset
            } else if (el.name === 'Regleta' && item.type === 'linkedItem') {
              // Manejo especial para regleta en linkedItems
              ;(item as LinkedItem).regleta = value
            } else {
              // if (el.name === 'Fondo' && item.tipoUnidNorm === 'V') return
              item[MorphDictionary?.[el.name]] = value
            }
          }
        })
      })
    })

    setTimeout(() => {
      const item = get().items.get(itemID)
      if (item && item.type === 'holes') {
        get().updateHole(itemID)
      }
      if (
        item &&
        item.type === 'linkedItem' &&
        (morphTarget === 'Alto' ||
          morphTarget === 'Ancho' ||
          morphTarget === 'Fondo' ||
          morphTarget === 'Regleta') &&
        item.childs &&
        item.childs.size > 0
      ) {
        get().updateLinkedSonPositions(itemID, morphTarget as 'Ancho' | 'Fondo' | 'Alto')
      }
    }, 0)
  },

  editMorphs: (morphTarget: Item['morphs'][number]['name'], value: number) => {
    set((store) => {
      const selectedItems = store.currentLayer[store.currentLayer].selected

      selectedItems.forEach((selectedId: string) => {
        const item = store.items.get(selectedId) as Item | LinkedItem | Hole
        if (!item) return

        const morphs = item.morphs
        if (!morphs) return

        morphs.forEach((el) => {
          if (el.name === morphTarget) {
            if (!item?.hasMorphs) {
              el._default = value
              return
            }
            el.default = value > el.max ? el.max : value < el.min ? el.min : value
            if (el.name === 'Fondo' && item.tipoUnidNorm === 'P') {
              item[MorphDictionary?.[el.name]] = value + item.offset
              return
            }
            item[MorphDictionary?.[el.name]] = value
          }
        })
      })
    })
  },
  deleteSceneObject: (elementID: string) => {
    set((store) => {
      const itemu = store.items.get(elementID)

      if (elementID.startsWith('linkedItem_')) {
        const item = store.items.get(elementID)
        if (!isLinkedItem(item)) return

        if (item.childrenOrder) {
          item.childrenOrder.forEach((preChildID) => {
            const [trash, childID] = preChildID.split('!')
            store.layers[store.currentLayer].selected.delete(childID)
            const GLBInfo = store.items.get(childID) as Item
            console.log(`Child GLBInfo: ${childID}`, GLBInfo)

            store.items.delete(childID)
            store.sceneBoundingBoxes.delete(childID)
            store.collisionableItems.delete(childID)
          })
        }

        store.items.delete(elementID)
        store.sceneBoundingBoxes.delete(elementID)
        store.collisionableItems.delete(elementID)
        store.currentObjectID = ''
        return
      }

      if (isWall(itemu)) {
        itemu?.holes.forEach((hole: string) => {
          store.items.delete(hole)
          store.sceneBoundingBoxes.delete(hole)
          store.collisionableItems.delete(hole)
          store.sceneBoundingBoxes.delete(getWallCOBB(hole))
          store.collisionableItems.delete(getWallCOBB(hole))
          store.layers[store.currentLayer].items.delete(hole)
          store.currentObjectID = ''
        })
        itemu.visible = false
        itemu.wallVisible = false
        return
      }

      if (isHole(itemu)) {
        const line = store.items.get(itemu.wall)
        if (isWall(line)) {
          line.holes.delete(elementID)
        }
      }

      if (isItem(itemu) && itemu.snappedWall) {
        const wall = store.items.get(itemu.snappedWall)
        if (isWall(wall)) {
          wall.lowItems.delete(elementID)
          wall.highItems.delete(elementID)
        }
      }

      store.layers[store.currentLayer].selected.delete(elementID)

      store.items.delete(elementID)
      store.sceneBoundingBoxes.delete(elementID)
      store.sceneBoundingBoxes.delete(getWallCOBB(elementID))
      store.collisionableItems.delete(getWallCOBB(elementID))
      store.collisionableItems.delete(elementID)
      store.currentObjectID = ''

      // console.log(`Deleted item: ${elementID}`)
    })
  },
  testFn: () => {
    set((store: Store) => {
      const object = store.items.get('someID')
      if (!object || object.type !== 'items') return
    })
  },
  openModal: () => {
    set((store: Store) => {
      store.isModalOpen = true
    })
  },
  closeModal: () => {
    set((store: Store) => {
      store.isModalOpen = false
    })
  },
  openProyect: () => {
    set((store: Store) => {
      store.isProjectOpened = true
    })
  },
  setImpuestos: (impuestos: Store['impuestos']) => {
    set((store) => {
      if (!impuestos) return
      store.impuestos = impuestos
      store.clientInfo.idImpuestos = impuestos.find((el) => el.porDefecto)?.idImpuestos ?? 0
    })
  },
  editCameraBoundingBox: (position: Vector3, rotation: Euler) => {
    set((store) => {
      const camObb = store.cameraOBB
      if (!camObb) return
      let dist = 0
      // Verificar si la posición está dentro del área formada por las paredes
      const walls = Array.from(store.items.values()).filter((item) => item.type === 'walls')
      const wallPoints = walls.flatMap((wall) => [wall.start, wall.end])
      // Algoritmo simple: proyectar en XZ y usar ray-casting para saber si está dentro del polígono
      const isPointInPolygon = (point: { x: number; z: number }, polygon: number[][]) => {
        const x = point.x,
          z = point.z
        let inside = false
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
          const xi = polygon[i][0],
            zi = polygon[i][1]
          const xj = polygon[j][0],
            zj = polygon[j][1]
          const intersect = zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi || 1) + xi
          if (intersect) inside = !inside
        }
        return inside
      }
      const polygon = wallPoints.map(([x, z]) => [x, z])
      if (!isPointInPolygon(position, polygon)) {
        // Calcular el centro del polígono en XZ
        let centerX = 0,
          centerZ = 0
        if (polygon.length > 0) {
          for (const [x, z] of polygon) {
            centerX += x
            centerZ += z
          }
          centerX /= polygon.length
          centerZ /= polygon.length
        }
        // La posición de la cámara es position.x, position.z
        // Calcular la distancia en XZ
        const dx = position.x - centerX
        const dz = position.z - centerZ
        dist = Math.sqrt(dx * dx + dz * dz)
      }
      if (typeof dist != 'number') {
        dist = Number(dist)
      }
      const m4 = new Matrix4().makeRotationFromEuler(rotation)
      const m3 = new Matrix3().setFromMatrix4(m4)
      camObb.set(position, toVector3([400, 50, dist]), m3)
    })
  },
  projectItemOnWall: (
    itemID: AnyID,
    wallID: AnyID,
    matrix: Matrix4,
    skipContiguityCheck = false,
  ) => {
    // LOG: Ver si se está llamando projectItemOnWall
    const itemBefore = get().items.get(itemID)
    if (itemBefore && itemBefore.matrix) {
      const posBefore = new Vector3()
      itemBefore.matrix.decompose(posBefore, new Quaternion(), new Vector3())
      console.log('🔄 [projectItemOnWall] ANTES de proyectar:', {
        itemID,
        wallID,
        position: { x: posBefore.x, y: posBefore.y, z: posBefore.z },
        itemProps: { x: itemBefore.x, y: itemBefore.y, z: itemBefore.z },
      })
    }

    set((store) => {
      const items = store.items
      const item = store.items.get(itemID) as Item | LinkedItem

      // Buscar el morph de ancho de manera más robusta
      const itemWidthMorph = item.morphs?.find(
        (morph) => morph.name === 'Ancho' || morph.name === 'width',
      )

      const itemWidth = item.long?.toLowerCase().includes('encimera b3200')
        ? (itemWidthMorph?._default ?? item.width ?? 0)
        : item.long !== 'Frontal' || item.long?.toLowerCase().includes('zocalo')
          ? (itemWidthMorph?.default ?? item.width ?? 0)
          : (itemWidthMorph?._default ?? item.width ?? 0)

      if (item.catalogID && item.catalogID.toString().startsWith('CORTEKIT')) {
        return // Saltar procesamiento para kits de corte
      }

      const wall = items.get(wallID) as Walls
      const { rotation, width } = wall
      const itemPosition = toVector3()
      const quaternion = toQuaternion()
      matrix.decompose(itemPosition, quaternion, toVector3())
      const end = toVector3(wall.end)
      const start = toVector3(wall.start)
      const wallDirection = end.clone().sub(start)
      const itemToStart = itemPosition.clone().sub(start)
      const projectedVector = itemToStart.projectOnVector(wallDirection.clone().normalize())

      // Calcular el centro del item
      const itemCenter2 = new Vector3(item.width / 2, item.height / 2, item.thickness / 2)

      // Aplicar la rotación al centro del item
      itemCenter2.applyQuaternion(quaternion).add(itemPosition)

      if (Math.abs(projectedVector.x) === 0) {
        projectedVector.x = 0
      }
      if (Math.abs(projectedVector.z) === 0) {
        projectedVector.z = 0
      }
      if (Math.abs(projectedVector.x) > width - itemWidth) {
        if (wallDirection.x < 0) {
          projectedVector.x = itemWidth - width
        } else {
          projectedVector.x = -itemWidth + width
        }
      }
      if (Math.abs(projectedVector.x) < 0) {
        projectedVector.x = 0
      }
      if (Math.abs(projectedVector.z) > width - itemWidth) {
        if (wallDirection.z < 0) {
          projectedVector.z = itemWidth - width
        } else {
          projectedVector.z = -itemWidth + width
        }
      }
      if (Math.abs(projectedVector.z) < 0) {
        projectedVector.z = -itemWidth
      }
      let length = projectedVector.length()
      const wallLength = wallDirection.length()
      let alongWall = projectedVector.length()
      if (projectedVector.dot(wallDirection.normalize()) < 0) {
        alongWall = 0
      } else if (alongWall > wallLength) {
        alongWall = wallLength - itemWidth
      }

      // Verificar si hay espacio disponible a la derecha (desde la posición proyectada hasta el final)
      const wallDirNormalized = wallDirection.clone().normalize()
      const spaceToEnd = wallLength - alongWall

      // Función auxiliar para verificar si hay otros muebles ocupando espacio
      // IMPORTANTE: Solo considera items del mismo tipoNormalizado (MBJ con MBJ, MAL con MAL)
      const checkSpaceAvailable = (positionAlongWall: number): boolean => {
        // Asegurar que la posición esté dentro de los límites de la pared
        if (positionAlongWall < 0) return false
        if (positionAlongWall + itemWidth > wallLength) return false

        const checkStart = positionAlongWall
        const checkEnd = positionAlongWall + itemWidth

        // Obtener el tipoNormalizado del item actual
        const itemTipo = item.tipoNormalizado

        // Obtener todos los muebles en la pared (excepto el actual)
        // IMPORTANTE: Filtrar por tipoNormalizado - solo considerar items del mismo tipo
        const allItemsOnWall = new Set([...wall.highItems, ...wall.lowItems])
        allItemsOnWall.delete(itemID)

        for (const otherItemID of allItemsOnWall) {
          const otherItem = items.get(otherItemID) as Item | LinkedItem
          if (!otherItem || !otherItem.snappedWall || otherItem.snappedWall !== wallID) continue

          // Solo considerar items del mismo tipoNormalizado
          // MBJ solo compite por espacio con MBJ/COL, MAL solo con MAL
          if (otherItem.tipoNormalizado !== itemTipo) {
            continue // Ignorar items de diferente tipo (no compiten por el mismo espacio horizontal)
          }

          const otherItemWidthMorph = otherItem.morphs?.find(
            (morph) => morph.name === 'Ancho' || morph.name === 'width',
          )
          const otherItemWidth = otherItem.long?.toLowerCase().includes('encimera b3200')
            ? (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
            : otherItem.long !== 'Frontal' || otherItem.long?.toLowerCase().includes('zocalo')
              ? (otherItemWidthMorph?.default ?? otherItem.width ?? 0)
              : (otherItemWidthMorph?._default ?? otherItem.width ?? 0)

          // Usar la matriz del mueble para mayor precisión (igual que en la búsqueda a la izquierda)
          const otherItemPos = new Vector3()
          const otherItemQuat = new Quaternion()
          otherItem.matrix.decompose(otherItemPos, otherItemQuat, new Vector3())

          const otherItemToStart = otherItemPos.clone().sub(start)
          const otherAlongWall = Math.max(0, otherItemToStart.dot(wallDirNormalized))

          const otherItemStart = otherAlongWall
          const otherItemEnd = otherItemStart + otherItemWidth

          // Verificar si hay solapamiento (los rangos se solapan)
          if (checkStart < otherItemEnd && checkEnd > otherItemStart) {
            return false // Hay solapamiento
          }
        }
        return true // No hay solapamiento
      }

      // **LÓGICA DE GRUPO CONTIGUO: Detectar grupo contiguo del mueble seleccionado**
      // Solo ejecutar si hay items seleccionados Y el item actual NO está seleccionado (para evitar interferir con el arrastre)
      // Y solo si NO se está saltando la verificación de contigüidad (arrastre manual)
      const selectedItems = store.layers[store.currentLayer].selected as Set<AnyID>
      let contiguousGroup: { firstItem: AnyID; lastItem: AnyID; items: AnyID[] } | null = null
      let selectedItemID: AnyID | null = null

      // Si skipContiguityCheck es true, saltar toda la lógica de grupos contiguos (arrastre manual)
      if (!skipContiguityCheck) {
        // Buscar grupo contiguo:
        // 1. Si hay otros items seleccionados, usar esos para encontrar el grupo
        // 2. Si solo el item actual está seleccionado, buscar el grupo más cercano en la misma pared
        const otherSelectedItems = Array.from(selectedItems).filter((id) => id !== itemID)

        if (otherSelectedItems.length > 0) {
          // Hay otros items seleccionados, buscar el grupo contiguo basándose en ellos
          selectedItemID = otherSelectedItems[0]
        } else {
          // No hay otros items seleccionados, buscar el grupo más cercano en la misma pared
          const allItemsOnWall = new Set([...wall.highItems, ...wall.lowItems])
          allItemsOnWall.delete(itemID)

          // Obtener todos los grupos contiguos
          const contiguousResult = get().getContiguousItems()

          // Encontrar el grupo más cercano al item actual en la misma pared
          let closestGroup: (Item | LinkedItem)[] | null = null
          let minDistance = Infinity

          for (const group of contiguousResult.groups) {
            // Verificar que el grupo esté en la misma pared y que el item actual NO esté en el grupo
            const groupItemIDs = group.map((i) => i.id)

            // Si el item está en el grupo, simplemente excluirlo del grupo y evaluar el resto
            // Esto permite que el item se coloque correctamente dentro del grupo
            let groupToEvaluate = group
            if (groupItemIDs.includes(itemID)) {
              // Crear un grupo temporal sin el item actual para evaluarlo
              groupToEvaluate = group.filter((i) => i.id !== itemID)
              if (groupToEvaluate.length === 0) {
                continue
              }
            }

            const firstGroupItem = items.get(groupToEvaluate[0].id) as Item | LinkedItem
            if (!firstGroupItem) {
              continue
            }
            if (firstGroupItem.snappedWall !== wallID) {
              continue
            }

            // IMPORTANTE: Solo considerar grupos del mismo tipoNormalizado
            // MBJ solo se agrupa con MBJ/COL, MAL solo con MAL
            const itemTipo = item.tipoNormalizado
            const groupTipo = firstGroupItem.tipoNormalizado
            if (itemTipo !== groupTipo) {
              // No agrupar items de diferentes tipos (MBJ con MAL, etc.)
              continue
            }

            // Calcular la distancia del item actual al grupo (usar el último item del grupo)
            const lastGroupItem = items.get(groupToEvaluate[groupToEvaluate.length - 1].id) as
              | Item
              | LinkedItem
            if (!lastGroupItem) continue

            const lastGroupItemPos = new Vector3()
            const lastGroupItemQuat = new Quaternion()
            lastGroupItem.matrix.decompose(lastGroupItemPos, lastGroupItemQuat, new Vector3())

            // Calcular distancia proyectada sobre la pared usando alongWall que ya está calculado
            // alongWall es la posición proyectada del item actual sobre la pared
            const lastGroupItemToStart = lastGroupItemPos.clone().sub(start)
            const lastGroupItemAlongWall = Math.max(0, lastGroupItemToStart.dot(wallDirNormalized))

            // Calcular dónde termina el último item del grupo usando la misma lógica que en la colocación
            const lastGroupItemWidthMorph = lastGroupItem.morphs?.find(
              (morph) => morph.name === 'Ancho' || morph.name === 'width',
            )
            const lastGroupItemWidth = lastGroupItem.long?.toLowerCase().includes('encimera b3200')
              ? (lastGroupItemWidthMorph?._default ?? lastGroupItem.width ?? 0)
              : lastGroupItem.long !== 'Frontal' ||
                  lastGroupItem.long?.toLowerCase().includes('zocalo')
                ? (lastGroupItemWidthMorph?.default ?? lastGroupItem.width ?? 0)
                : (lastGroupItemWidthMorph?._default ?? lastGroupItem.width ?? 0)

            const lastGroupItemAngle = radians(
              get().getInitialAngleDegrees(groupToEvaluate[groupToEvaluate.length - 1].id),
            )
            const lastGroupItemCos = Math.cos(lastGroupItemAngle)
            const lastGroupItemSin = Math.sin(lastGroupItemAngle)

            const lastGroupItemP = { x: lastGroupItemPos.x, z: lastGroupItemPos.z }
            const lastGroupItemVertices = [
              { x: 0, z: 0 },
              { x: lastGroupItemWidth, z: 0 },
              {
                x: lastGroupItemWidth,
                z: (lastGroupItem.thickness || 0) + ((lastGroupItem as LinkedItem).regleta || 0),
              },
              {
                x: 0,
                z: (lastGroupItem.thickness || 0) + ((lastGroupItem as LinkedItem).regleta || 0),
              },
            ].map((vertex) => ({
              x: lastGroupItemP.x + vertex.x * lastGroupItemCos - vertex.z * lastGroupItemSin,
              z: lastGroupItemP.z + vertex.x * lastGroupItemSin + vertex.z * lastGroupItemCos,
            }))

            let lastGroupItemMaxAlongWall = 0
            for (const vertex of lastGroupItemVertices) {
              const vertexToStart = toVector3([vertex.x, 0, vertex.z]).sub(start)
              const vertexAlongWall = Math.max(0, vertexToStart.dot(wallDirNormalized))
              lastGroupItemMaxAlongWall = Math.max(lastGroupItemMaxAlongWall, vertexAlongWall)
            }

            // Calcular distancia desde la posición proyectada del item actual hasta donde termina el último item del grupo
            // Si el item está a la derecha del grupo, la distancia es positiva
            // Si está a la izquierda, la distancia es negativa
            const distance = alongWall - lastGroupItemMaxAlongWall

            // Preferir grupos que están a la izquierda del item (distancia positiva pequeña)
            // o grupos muy cercanos (distancia pequeña en valor absoluto)
            const absDistance = Math.abs(distance)

            // Si el item está cerca del grupo (menos de 500mm) o está a la derecha del grupo
            if (absDistance < minDistance || (distance > 0 && distance < 1000)) {
              minDistance = absDistance
              closestGroup = groupToEvaluate
              selectedItemID = groupToEvaluate[0].id
            }
          }

          // Aumentar el umbral para asegurar que siempre encuentre grupos cercanos
          // El umbral de 5000mm (5 metros) es razonable para una pared
          if (!(closestGroup && minDistance < 5000)) {
            selectedItemID = null
          }
        }
      }

      if (selectedItemID && !skipContiguityCheck) {
        const selectedItem = items.get(selectedItemID) as Item | LinkedItem

        if (selectedItem && selectedItem.snappedWall === wallID) {
          // Verificar que el tipoNormalizado del item actual coincida con el del selectedItem
          // MAL solo se agrupa con MAL, MBJ/COL solo con MBJ/COL
          const itemTipo = item.tipoNormalizado
          const selectedTipo = selectedItem.tipoNormalizado
          if (
            (itemTipo === 'MAL' && (selectedTipo === 'MBJ' || selectedTipo === 'COL')) ||
            ((itemTipo === 'MBJ' || itemTipo === 'COL') && selectedTipo === 'MAL')
          ) {
            // No agrupar si son de diferentes alturas
            selectedItemID = null
          }
        }

        if (selectedItemID && selectedItem && selectedItem.snappedWall === wallID) {
          // Obtener el grupo contiguo usando getContiguousItems
          const contiguousResult = get().getContiguousItems()
          const group = contiguousResult.groups.find((g) => g.some((i) => i.id === selectedItemID))

          if (group && group.length > 0) {
            // El grupo ya está ordenado, el primero es [0] y el último es [length-1]
            const firstItemID = group[0].id
            const lastItemID = group[group.length - 1].id
            const groupItemIDs = group.map((i) => i.id)

            // Si hay un selectedItemID, siempre usar el grupo para determinar la posición
            // La lógica será: si hay espacio a la derecha del último, colocar ahí; si no, a la izquierda del primero
            // No importa cuál item esté seleccionado, siempre se usa el último del grupo como referencia
            if (!groupItemIDs.includes(itemID) || selectedItemID) {
              contiguousGroup = {
                firstItem: firstItemID,
                lastItem: lastItemID,
                items: groupItemIDs,
              }
            }
          }
        }
      }

      // **LÓGICA DE COLOCACIÓN BASADA EN GRUPO CONTIGUO**
      let placedInContiguousGroup = false
      if (contiguousGroup) {
        const lastItem = items.get(contiguousGroup.lastItem) as Item | LinkedItem
        const firstItem = items.get(contiguousGroup.firstItem) as Item | LinkedItem

        if (
          lastItem &&
          firstItem &&
          lastItem.snappedWall === wallID &&
          firstItem.snappedWall === wallID
        ) {
          // Calcular dónde termina el último mueble del grupo
          const lastItemPos = new Vector3()
          const lastItemQuat = new Quaternion()
          lastItem.matrix.decompose(lastItemPos, lastItemQuat, new Vector3())

          const lastItemWidthMorph = lastItem.morphs?.find(
            (morph) => morph.name === 'Ancho' || morph.name === 'width',
          )
          const lastItemWidth = lastItem.long?.toLowerCase().includes('encimera b3200')
            ? (lastItemWidthMorph?._default ?? lastItem.width ?? 0)
            : lastItem.long !== 'Frontal' || lastItem.long?.toLowerCase().includes('zocalo')
              ? (lastItemWidthMorph?.default ?? lastItem.width ?? 0)
              : (lastItemWidthMorph?._default ?? lastItem.width ?? 0)

          // Calcular los vértices del último mueble para obtener dónde termina realmente
          const lastItemAngle = radians(get().getInitialAngleDegrees(contiguousGroup.lastItem))
          const lastItemCos = Math.cos(lastItemAngle)
          const lastItemSin = Math.sin(lastItemAngle)

          const lastItemP = { x: lastItemPos.x, z: lastItemPos.z }
          const lastItemVertices = [
            { x: 0, z: 0 },
            { x: lastItemWidth, z: 0 },
            {
              x: lastItemWidth,
              z: (lastItem.thickness || 0) + ((lastItem as LinkedItem).regleta || 0),
            },
            { x: 0, z: (lastItem.thickness || 0) + ((lastItem as LinkedItem).regleta || 0) },
          ].map((vertex) => ({
            x: lastItemP.x + vertex.x * lastItemCos - vertex.z * lastItemSin,
            z: lastItemP.z + vertex.x * lastItemSin + vertex.z * lastItemCos,
          }))

          let lastItemMaxAlongWall = 0
          for (const vertex of lastItemVertices) {
            const vertexToStart = toVector3([vertex.x, 0, vertex.z]).sub(start)
            const vertexAlongWall = Math.max(0, vertexToStart.dot(wallDirNormalized))
            lastItemMaxAlongWall = Math.max(lastItemMaxAlongWall, vertexAlongWall)
          }

          const positionAfterLast = lastItemMaxAlongWall

          // Función especial para verificar espacio excluyendo el último mueble del grupo
          const checkSpaceAvailableExcludingLast = (positionAlongWall: number): boolean => {
            if (positionAlongWall < 0) return false
            if (positionAlongWall + itemWidth > wallLength) return false

            const checkStart = positionAlongWall
            const checkEnd = positionAlongWall + itemWidth

            // Obtener el tipoNormalizado del item actual
            const itemTipo = item.tipoNormalizado

            // Obtener todos los muebles en la pared (excepto el actual Y el último del grupo)
            const allItemsOnWall = new Set([...wall.highItems, ...wall.lowItems])
            allItemsOnWall.delete(itemID)
            allItemsOnWall.delete(contiguousGroup.lastItem) // Excluir el último mueble del grupo

            for (const otherItemID of allItemsOnWall) {
              const otherItem = items.get(otherItemID) as Item | LinkedItem
              if (!otherItem || !otherItem.snappedWall || otherItem.snappedWall !== wallID) continue

              // Solo considerar items del mismo tipoNormalizado
              if (otherItem.tipoNormalizado !== itemTipo) {
                continue // Ignorar items de diferente tipo
              }

              const otherItemWidthMorph = otherItem.morphs?.find(
                (morph) => morph.name === 'Ancho' || morph.name === 'width',
              )
              const otherItemWidth = otherItem.long?.toLowerCase().includes('encimera b3200')
                ? (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
                : otherItem.long !== 'Frontal' || otherItem.long?.toLowerCase().includes('zocalo')
                  ? (otherItemWidthMorph?.default ?? otherItem.width ?? 0)
                  : (otherItemWidthMorph?._default ?? otherItem.width ?? 0)

              const otherItemPos = new Vector3()
              const otherItemQuat = new Quaternion()
              otherItem.matrix.decompose(otherItemPos, otherItemQuat, new Vector3())

              const otherItemToStart = otherItemPos.clone().sub(start)
              const otherAlongWall = Math.max(0, otherItemToStart.dot(wallDirNormalized))

              const otherItemStart = otherAlongWall
              const otherItemEnd = otherItemStart + otherItemWidth

              // Verificar si hay solapamiento (los rangos se solapan)
              if (checkStart < otherItemEnd && checkEnd > otherItemStart) {
                return false // Hay solapamiento
              }
            }
            return true // No hay solapamiento
          }

          // 1. Intentar primero a la derecha del último mueble
          // IMPORTANTE: positionAfterLast es donde TERMINA el último mueble, que es donde debe EMPEZAR el nuevo mueble
          // El punto de pivote del nuevo mueble está en su esquina inferior izquierda, así que colocarlo en positionAfterLast es correcto
          const hasSpaceRight = positionAfterLast + itemWidth <= wallLength
          const spaceAvailableRight = checkSpaceAvailableExcludingLast(positionAfterLast)

          if (hasSpaceRight && spaceAvailableRight) {
            // Usar la posición exacta donde termina el último mueble del grupo
            // Esto asegura que el nuevo mueble quede pegado exactamente
            // IMPORTANTE: Usar exactamente lastItemMaxAlongWall sin redondeos ni ajustes
            alongWall = lastItemMaxAlongWall
            placedInContiguousGroup = true
          } else {
            // 2. Si no cabe a la derecha, intentar a la izquierda del primer mueble
            const firstItemPos = new Vector3()
            const firstItemQuat = new Quaternion()
            firstItem.matrix.decompose(firstItemPos, firstItemQuat, new Vector3())

            const firstItemToStart = firstItemPos.clone().sub(start)
            const firstItemAlongWall = Math.max(0, firstItemToStart.dot(wallDirNormalized))

            const positionBeforeFirst = firstItemAlongWall - itemWidth

            // Función especial para verificar espacio excluyendo el primer mueble del grupo
            const checkSpaceAvailableExcludingFirst = (positionAlongWall: number): boolean => {
              if (positionAlongWall < 0) return false
              if (positionAlongWall + itemWidth > wallLength) return false

              const checkStart = positionAlongWall
              const checkEnd = positionAlongWall + itemWidth

              // Obtener el tipoNormalizado del item actual
              const itemTipo = item.tipoNormalizado

              const allItemsOnWall = new Set([...wall.highItems, ...wall.lowItems])
              allItemsOnWall.delete(itemID)
              allItemsOnWall.delete(contiguousGroup.firstItem) // Excluir el primer mueble del grupo

              for (const otherItemID of allItemsOnWall) {
                const otherItem = items.get(otherItemID) as Item | LinkedItem
                if (!otherItem || !otherItem.snappedWall || otherItem.snappedWall !== wallID)
                  continue

                // Solo considerar items del mismo tipoNormalizado
                if (otherItem.tipoNormalizado !== itemTipo) {
                  continue // Ignorar items de diferente tipo
                }

                const otherItemWidthMorph = otherItem.morphs?.find(
                  (morph) => morph.name === 'Ancho' || morph.name === 'width',
                )
                const otherItemWidth = otherItem.long?.toLowerCase().includes('encimera b3200')
                  ? (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
                  : otherItem.long !== 'Frontal' || otherItem.long?.toLowerCase().includes('zocalo')
                    ? (otherItemWidthMorph?.default ?? otherItem.width ?? 0)
                    : (otherItemWidthMorph?._default ?? otherItem.width ?? 0)

                const otherItemPos = new Vector3()
                const otherItemQuat = new Quaternion()
                otherItem.matrix.decompose(otherItemPos, otherItemQuat, new Vector3())

                const otherItemToStart = otherItemPos.clone().sub(start)
                const otherAlongWall = Math.max(0, otherItemToStart.dot(wallDirNormalized))

                const otherItemStart = otherAlongWall
                const otherItemEnd = otherItemStart + otherItemWidth

                if (checkStart < otherItemEnd && checkEnd > otherItemStart) {
                  return false
                }
              }
              return true
            }

            if (
              positionBeforeFirst >= 0 &&
              checkSpaceAvailableExcludingFirst(positionBeforeFirst)
            ) {
              alongWall = positionBeforeFirst
              placedInContiguousGroup = true
            }
          }
        }
      }

      // **NUEVA LÓGICA: FORZAR CONTIGÜIDAD CUANDO SE COLOCA A LA IZQUIERDA**
      const forceContiguity = (targetPosition: number): number => {
        // Obtener el tipoNormalizado del item actual
        const itemTipo = item.tipoNormalizado

        // Buscar el mueble más cercano a la derecha de esta posición
        let closestRightItem: { id: AnyID; startPos: number; width: number } | null = null
        let minDistance = Infinity

        const allItemsOnWallForContiguity = new Set([...wall.highItems, ...wall.lowItems])
        allItemsOnWallForContiguity.delete(itemID)

        for (const otherItemID of allItemsOnWallForContiguity) {
          const otherItem = items.get(otherItemID) as Item | LinkedItem
          if (!otherItem || !otherItem.snappedWall || otherItem.snappedWall !== wallID) continue

          // Solo considerar items del mismo tipoNormalizado
          if (otherItem.tipoNormalizado !== itemTipo) {
            continue // Ignorar items de diferente tipo
          }

          const otherItemWidthMorph = otherItem.morphs?.find(
            (morph) => morph.name === 'Ancho' || morph.name === 'width',
          )
          const otherItemWidth = otherItem.long?.toLowerCase().includes('encimera b3200')
            ? (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
            : otherItem.long !== 'Frontal' || otherItem.long?.toLowerCase().includes('zocalo')
              ? (otherItemWidthMorph?.default ?? otherItem.width ?? 0)
              : (otherItemWidthMorph?._default ?? otherItem.width ?? 0)

          const otherItemPos = new Vector3()
          const otherItemQuat = new Quaternion()
          otherItem.matrix.decompose(otherItemPos, otherItemQuat, new Vector3())

          const otherItemToStart = otherItemPos.clone().sub(start)
          const otherAlongWall = Math.max(0, otherItemToStart.dot(wallDirNormalized))

          // Si está a la derecha de nuestra posición
          if (otherAlongWall > targetPosition) {
            const distance = otherAlongWall - targetPosition
            if (distance < minDistance) {
              minDistance = distance
              closestRightItem = {
                id: otherItemID,
                startPos: otherAlongWall,
                width: otherItemWidth,
              }
            }
          }
        }

        // Si hay un mueble a la derecha, pegarnos a él
        if (closestRightItem && minDistance > 0) {
          // Calcular posición pegada al mueble de la derecha
          const newPosition = closestRightItem.startPos - itemWidth

          // Asegurar que no solape con muebles a la izquierda
          let leftConflict = false
          for (const otherItemID of allItemsOnWallForContiguity) {
            const otherItem = items.get(otherItemID) as Item | LinkedItem
            if (!otherItem || !otherItem.snappedWall || otherItem.snappedWall !== wallID) continue

            // Solo considerar items del mismo tipoNormalizado
            if (otherItem.tipoNormalizado !== itemTipo) {
              continue // Ignorar items de diferente tipo
            }

            const otherItemWidthMorph = otherItem.morphs?.find(
              (morph) => morph.name === 'Ancho' || morph.name === 'width',
            )
            const otherItemWidth = otherItem.long?.toLowerCase().includes('encimera b3200')
              ? (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
              : otherItem.long !== 'Frontal' || otherItem.long?.toLowerCase().includes('zocalo')
                ? (otherItemWidthMorph?.default ?? otherItem.width ?? 0)
                : (otherItemWidthMorph?._default ?? otherItem.width ?? 0)

            const otherItemPos = new Vector3()
            const otherItemQuat = new Quaternion()
            otherItem.matrix.decompose(otherItemPos, otherItemQuat, new Vector3())

            const otherItemToStart = otherItemPos.clone().sub(start)
            const otherAlongWall = Math.max(0, otherItemToStart.dot(wallDirNormalized))
            const otherItemEnd = otherAlongWall + otherItemWidth

            // Verificar solapamiento
            if (newPosition < otherItemEnd && newPosition + itemWidth > otherAlongWall) {
              leftConflict = true
              break
            }
          }

          if (!leftConflict && newPosition >= 0) {
            return newPosition
          }
        }

        return targetPosition
      }

      // Si se colocó en el grupo contiguo, saltar la lógica estándar
      // Si no hay espacio suficiente a la derecha Y no se colocó en grupo contiguo, intentar colocar desde el final hacia atrás (izquierda)
      if (!placedInContiguousGroup && (spaceToEnd < itemWidth || !checkSpaceAvailable(alongWall))) {
        // Obtener todos los muebles en la pared (excepto el actual)
        const allItemsOnWall = new Set([...wall.highItems, ...wall.lowItems])
        allItemsOnWall.delete(itemID)

        // Encontrar el mueble más cercano a la izquierda de la posición proyectada que tenga espacio después
        let bestPosition: number | null = null
        let closestItemToLeft: { id: AnyID; endPosition: number } | null = null
        let minDistance = Infinity

        for (const otherItemID of allItemsOnWall) {
          const otherItem = items.get(otherItemID) as Item | LinkedItem
          if (!otherItem || !otherItem.snappedWall || otherItem.snappedWall !== wallID) continue

          const otherItemWidthMorph = otherItem.morphs?.find(
            (morph) => morph.name === 'Ancho' || morph.name === 'width',
          )
          const otherItemWidth = otherItem.long?.toLowerCase().includes('encimera b3200')
            ? (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
            : otherItem.long !== 'Frontal' || otherItem.long?.toLowerCase().includes('zocalo')
              ? (otherItemWidthMorph?.default ?? otherItem.width ?? 0)
              : (otherItemWidthMorph?._default ?? otherItem.width ?? 0)

          // Calcular correctamente dónde termina el mueble a lo largo de la pared
          // Usar la misma lógica que areContiguous para calcular los vértices y obtener el máximo
          const otherItemPos = new Vector3()
          const otherItemQuat = new Quaternion()
          otherItem.matrix.decompose(otherItemPos, otherItemQuat, new Vector3())

          // Calcular los vértices del mueble usando la misma lógica que areContiguous
          const otherItemAngle = radians(get().getInitialAngleDegrees(otherItemID))
          const otherItemCos = Math.cos(otherItemAngle)
          const otherItemSin = Math.sin(otherItemAngle)

          // Calcular los vértices del mueble (igual que en areContiguous)
          const otherItemP = { x: otherItemPos.x, z: otherItemPos.z }
          const otherItemVertices = [
            { x: 0, z: 0 },
            { x: otherItemWidth, z: 0 },
            { x: otherItemWidth, z: (otherItem.thickness || 0) + (otherItem.regleta || 0) },
            { x: 0, z: (otherItem.thickness || 0) + (otherItem.regleta || 0) },
          ].map((vertex) => ({
            x: otherItemP.x + vertex.x * otherItemCos - vertex.z * otherItemSin,
            z: otherItemP.z + vertex.x * otherItemSin + vertex.z * otherItemCos,
          }))

          // Proyectar todos los vértices en la dirección de la pared y encontrar el máximo (donde termina)
          let otherItemMaxAlongWall = 0
          for (const vertex of otherItemVertices) {
            const vertexToStart = toVector3([vertex.x, 0, vertex.z]).sub(start)
            const vertexAlongWall = Math.max(0, vertexToStart.dot(wallDirNormalized))
            otherItemMaxAlongWall = Math.max(otherItemMaxAlongWall, vertexAlongWall)
          }

          // Usar el máximo sin ajuste para obtener la posición exacta donde termina
          const otherItemEnd = otherItemMaxAlongWall

          // Si el mueble está a la izquierda de la posición proyectada
          if (otherItemEnd <= alongWall) {
            const distance = alongWall - otherItemEnd
            // Verificar que haya espacio suficiente después de este mueble para colocar el nuevo
            // Usar otherItemEnd directamente (sin ajuste) para la verificación de espacio
            const positionAfterItem = otherItemEnd
            if (
              positionAfterItem + itemWidth <= wallLength &&
              checkSpaceAvailable(positionAfterItem)
            ) {
              if (distance < minDistance) {
                minDistance = distance
                // Guardar la posición exacta donde termina el mueble, pero ajustar ligeramente al colocar para eliminar el espacio
                closestItemToLeft = { id: otherItemID, endPosition: otherItemEnd }
                bestPosition = positionAfterItem
              }
            }
          }
        }

        // Si encontramos un mueble a la izquierda con espacio, colocar el nuevo mueble pegado a él
        if (bestPosition !== null && closestItemToLeft) {
          // Verificar espacio disponible JUSTO después del mueble
          const positionAfterItem = closestItemToLeft.endPosition
          const spaceCheckPosition = positionAfterItem

          if (checkSpaceAvailable(spaceCheckPosition)) {
            // Si hay espacio, colocar pegado exactamente donde termina el otro mueble
            alongWall = positionAfterItem
          } else {
            // Buscar espacio disponible después del mueble
            let foundSpace = false
            let searchPosition = positionAfterItem + 10 // Comenzar 10mm después

            while (searchPosition <= wallLength - itemWidth && !foundSpace) {
              if (checkSpaceAvailable(searchPosition)) {
                alongWall = searchPosition
                foundSpace = true
              }
              searchPosition += 10
            }

            if (!foundSpace) {
              alongWall = Math.max(0, wallLength - itemWidth)
            }
          }

          // Asegurarse de que el mueble quepa en la pared
          if (alongWall + itemWidth > wallLength) {
            alongWall = Math.max(0, wallLength - itemWidth)
          }
        } else {
          // No hay muebles a la izquierda con espacio, buscar desde el final hacia atrás
          let leftAlongWall = Math.max(0, wallLength - itemWidth)
          let foundSpace = false

          while (leftAlongWall >= 0 && !foundSpace) {
            if (checkSpaceAvailable(leftAlongWall)) {
              foundSpace = true
              alongWall = leftAlongWall
            } else {
              leftAlongWall -= 10
            }
          }

          // Si no se encontró espacio desde el final, buscar desde el inicio
          if (!foundSpace) {
            let searchPosition = 0
            while (searchPosition <= wallLength - itemWidth) {
              if (checkSpaceAvailable(searchPosition)) {
                alongWall = searchPosition
                foundSpace = true
                break
              }
              searchPosition += 10
            }
            // Si aún no hay espacio, usar la posición original (puede solaparse, pero es mejor que nada)
            if (!foundSpace) {
              alongWall = projectedVector.length()
              if (alongWall > wallLength - itemWidth) {
                alongWall = Math.max(0, wallLength - itemWidth)
              }
            }
          }
        }

        // **DESPUÉS de encontrar alongWall, FORZAR CONTIGÜIDAD**
        // Solo aplicar forceContiguity si NO se colocó en un grupo contiguo Y NO se está saltando la verificación
        if (!placedInContiguousGroup && !skipContiguityCheck) {
          alongWall = forceContiguity(alongWall)
        }
      }

      // Calcular la posición ajustada directamente sobre la pared (mantiene el snap)
      const adjustedPosition = start
        .clone()
        .add(wallDirNormalized.clone().multiplyScalar(alongWall))

      // Calcular el centro del item
      const itemCenter = new Vector3(
        item.width / 2,
        item.height / 2 + itemPosition.y,
        item.thickness / 2,
      )

      // Aplicar la rotación al centro del item
      itemCenter
        .applyQuaternion(toQuaternion().setFromEuler(new Euler(0, -rotation, 0, 'XYZ')))
        .add(adjustedPosition)

      // Convertir la rotación del quaternion a una Matrix4 y extraer la Matrix3
      const neweuler = new Euler(0, -rotation, 0, 'XYZ')
      const rotationMatrix4 = new Matrix4().makeRotationFromEuler(neweuler)
      const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

      if (length < 0) {
        length = 0
      }
      if (length > width || length + item.width > width) {
        length = width
      }
      if (!item) return

      // Aplicar restricción de altura en modo 3D
      // IMPORTANTE: Si es un MAL sobre un MBJ, la posición Y ya viene correctamente calculada
      // desde getRelativeMatrix o collideItems, NO debemos sobrescribirla
      let restrictedY = itemPosition.y
      const mode = store.mode
      const originalY = itemPosition.y

      // Verificar si hay un MBJ debajo de este MAL (para preservar la posición Y)
      let isMALOverMBJ = false
      let isMALOverCOL = false
      if (item.tipoNormalizado === 'MAL') {
        const allItemsOnWall = new Set([...wall.highItems, ...wall.lowItems])
        allItemsOnWall.delete(itemID)

        for (const otherItemID of allItemsOnWall) {
          const otherItem = items.get(otherItemID) as Item | LinkedItem
          if (!otherItem || otherItem.snappedWall !== wallID) continue

          if (otherItem.tipoNormalizado === 'MBJ') {
            // Verificar si están en la misma posición X/Z (mismo lugar en la pared)
            const otherItemPos = new Vector3()
            otherItem.matrix.decompose(otherItemPos, new Quaternion(), new Vector3())

            const horizontalDistance = Math.sqrt(
              Math.pow(itemPosition.x - otherItemPos.x, 2) +
                Math.pow(itemPosition.z - otherItemPos.z, 2),
            )

            // Si están muy cerca horizontalmente (menos de 100mm), es probable que el MAL esté sobre el MBJ
            if (horizontalDistance < 100) {
              isMALOverMBJ = true
              break
            }
          } else if (otherItem.tipoNormalizado === 'COL') {
            // Verificar si están en la misma posición X/Z (mismo lugar en la pared)
            const otherItemPos = new Vector3()
            otherItem.matrix.decompose(otherItemPos, new Quaternion(), new Vector3())

            const horizontalDistance = Math.sqrt(
              Math.pow(itemPosition.x - otherItemPos.x, 2) +
                Math.pow(itemPosition.z - otherItemPos.z, 2),
            )

            // Si están muy cerca horizontalmente (menos de 100mm), es probable que el MAL esté sobre el COL
            if (horizontalDistance < 100) {
              isMALOverCOL = true
              break
            }
          }
        }
      }

      const isMALOverMBJOrCOL = isMALOverMBJ || isMALOverCOL

      if (mode === 'MODE_3D_VIEW' && !isMALOverMBJOrCOL) {
        // Solo aplicar restricciones si NO es un MAL sobre MBJ/COL
        // Restringir mínimo a 0 siempre
        restrictedY = Math.max(0, restrictedY)

        // Si el item tiene altura, aplicar restricción máxima
        if (item.height) {
          // Usar la altura de la pared a la que está pegado
          const maxWallHeight = wall.height

          // Restringir máximo considerando la altura del mueble
          const maxYAllowed = maxWallHeight - item.height
          restrictedY = Math.min(restrictedY, maxYAllowed)
        }
      } else if (isMALOverMBJOrCOL) {
        // Si es un MAL sobre MBJ/COL, mantener la posición Y original (ya viene correcta)
        restrictedY = originalY
      }

      item.matrix.makeRotationFromEuler(new Euler(0, -rotation, 0, 'XYZ'))
      if (!item.visible) item.visible = true
      item.matrix.setPosition(adjustedPosition.x, restrictedY, adjustedPosition.z)
      item.x = adjustedPosition.x
      item.z = adjustedPosition.z
      item.y = restrictedY
      item.snappedWall = wallID

      // LOG: Ver la nueva posición después de proyectar
      // console.log('📍 [projectItemOnWall] DESPUÉS de proyectar:', {
      //   itemID,
      //   wallID,
      //   newPosition: { x: adjustedPosition.x, y: restrictedY, z: adjustedPosition.z },
      //   itemProps: { x: item.x, y: item.y, z: item.z },
      // })

      // Si se colocó en un grupo contiguo, incrementar contador para forzar recálculo de texturas
      if (placedInContiguousGroup) {
        store.contadorModificaciones = (store.contadorModificaciones || 0) + 1
      }

      if (item.canCollide !== false && item.tipoNormalizado === 'MAL') {
        wall.highItems.add(itemID)
      }
      if (
        item.canCollide !== false &&
        (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL')
      ) {
        wall.lowItems.add(itemID)
      }
      store.sceneBoundingBoxes.set(itemID, {
        boundingBox: new OBB(
          itemCenter,
          toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
          rotationMatrix,
        ),
        type: 'items',
      })

      // Forzar actualización de texturas de encimera después de colocar el mueble
      // Esto asegura que los grupos contiguos se recalculen y la encimera se actualice
      store.contadorModificaciones = store.contadorModificaciones + 1
    })
  },
  editZone: async (
    id: AnyID,
    zoneToPasteTo: string,
    _copiedMaterial: Zone[string]['material'],
    skipCounterIncrement = false,
  ) => {
    if (skipCounterIncrement) {
      pushHistorySuppress()
    }

    const applyZoneEdit = () => {
      set((store) => {
      const sceneObject = store.items.get(id) ?? store.areas.get(id)
      if (!sceneObject) return

      const textureIndex = sceneObject.textures.findIndex((el) => el.zoneName === zoneToPasteTo)
      if (textureIndex === -1) {
        return
      }
      if (!_copiedMaterial) return console.log('Not material found', { _copiedMaterial })

      // Aplicar el nuevo material completamente, reemplazando el anterior
      // IMPORTANTE: Para la zona "Estructura", SIEMPRE usar los valores del backend directamente
      // NO preservar valores anteriores porque el backend tiene los valores correctos
      // El material copiado (_copiedMaterial) viene de getStandardMaterial que mapea brillo->roughness, metal->metalness, reflejo->envMapIntensity
      // Manejar null como undefined para usar fallbacks correctamente
      const roughnessValue = _copiedMaterial?.roughness != null ? _copiedMaterial.roughness : 1
      const metalnessValue = _copiedMaterial?.metalness != null ? _copiedMaterial.metalness : 0
      const envMapIntensityValue =
        _copiedMaterial?.envMapIntensity != null ? _copiedMaterial.envMapIntensity : 0
      const opacityValue = _copiedMaterial?.opacity != null ? _copiedMaterial.opacity : 1
      const cleanUrl = _copiedMaterial?.url?.split('?')[0] || _copiedMaterial?.url

      sceneObject.zones[zoneToPasteTo].material = {
        ..._copiedMaterial,
        // Usar valores del backend directamente, con fallbacks solo si son null/undefined
        roughness: roughnessValue,
        metalness: metalnessValue,
        envMapIntensity: envMapIntensityValue,
        opacity: opacityValue,
        color: resolveApiTextureColor(_copiedMaterial?.color, Boolean(cleanUrl)),
        displacementScale: _copiedMaterial?.displacementScale ?? 0.01,
        img: _copiedMaterial?.img || null,
        transparent: opacityValue < 1,
        depthWrite: opacityValue >= 1,
      }

      // Guardar el material para herencia (solo si es Estructura)
      const textureInfo = sceneObject.textures.find((t: any) => t.zoneName === zoneToPasteTo)
      if (textureInfo?.zoneDisplayName === 'Estructura') {
        ;(store as any).selectedEstructuraMaterial = {
          ...sceneObject.zones[zoneToPasteTo].material,
        }
      }

      // Crear un nuevo objeto de textura para que React detecte el cambio
      // Esto es crítico para que ObjectMesh reciba la versión actualizada
      const previousTexture = sceneObject.textures[textureIndex]

      // Crear un nuevo array de texturas para que React detecte el cambio
      // Esto es crítico para que Item3D y ObjectMesh re-rendericen correctamente
      const newTextures = [...sceneObject.textures]

      // Priorizar los datos de la textura nueva si vienen informados.
      const nextAcabado = _copiedMaterial?.acabado ?? previousTexture?.acabado ?? null
      const nextModelo = _copiedMaterial?.modelo ?? previousTexture?.modelo ?? null
      const nextGroupID = _copiedMaterial?.groupID ?? previousTexture?.groupID
      const nextSearchID = _copiedMaterial?.searchID ?? previousTexture?.searchID
      const nextTextureId = _copiedMaterial?.id ?? previousTexture?.id
      const nextLinkedTextureID =
        _copiedMaterial?.linkedTextureID ?? previousTexture?.linkedTextureID
      const nextIdAcabadoUnidadModeloAcabado =
        _copiedMaterial?.idAcabadoUnidadModeloAcabado ??
        previousTexture?.idAcabadoUnidadModeloAcabado ??
        (nextLinkedTextureID ? `${zoneToPasteTo}_${nextLinkedTextureID}` : undefined)

      newTextures[textureIndex] = {
        ...sceneObject.textures[textureIndex],
        groupID: nextGroupID,
        name: _copiedMaterial?.name,
        searchID: nextSearchID,
        url: cleanUrl, // Guardar URL limpia sin parámetros de query
        id: nextTextureId,
        linkedTextureID: nextLinkedTextureID,
        idAcabadoUnidadModeloAcabado: nextIdAcabadoUnidadModeloAcabado,
        modelo: nextModelo,
        acabado: nextAcabado,
      }
      // Reemplazar el array completo para que React detecte el cambio
      sceneObject.textures = newTextures

      // Incrementar contador para forzar re-render de componentes 3D
      // IMPORTANTE: No incrementar si es una herencia para evitar re-render de items existentes
      if (!skipCounterIncrement) {
        store.contadorModificaciones = store.contadorModificaciones + 1
      }

      // Invalidar React Three Fiber para forzar re-render inmediato
      // Esto asegura que los cambios de textura se vean inmediatamente sin necesidad de hover
      try {
        const r3f = typeof window !== 'undefined' ? (window as any).r3f : undefined
        if (r3f?.invalidate) {
          r3f.invalidate()
          // Invalidar también en el siguiente frame para asegurar que se procese
          requestAnimationFrame(() => {
            if (r3f?.invalidate) {
              r3f.invalidate()
            }
          })
        }
      } catch (error) {
        // Silenciar errores si r3f no está disponible
      }
    })
    }

    const shouldRecordHistory =
      !skipCounterIncrement &&
      !isHistoryRecordingSuppressed() &&
      getHistoryBatchDepth() === 0 &&
      !isHistoryPaused()

    if (shouldRecordHistory) {
      get().recordDestructiveChange(applyZoneEdit)
    } else {
      applyZoneEdit()
    }

    if (skipCounterIncrement) {
      popHistorySuppress()
    }
  },
  toggleVisibility: (elementID: string) => {
    set((store) => {
      const item = store.items.get(elementID) ?? store.areas.get(elementID)
      if (item) {
        item.visible = !item.visible
      }
    })
  },
  toggleCollision: (id: string) => {
    set((store) => {
      const item = store.items.get(id)
      if (!item || !id) return console.log('Cant find item on collision')
      item.canCollide = !item?.canCollide
      const collisionableItems = store.collisionableItems
      if (!item.canCollide) {
        collisionableItems.delete(id)
      } else {
        collisionableItems.add(id)
      }
    })
  },
  toggleIsBlocked: (selectedItems: Set<string>) => {
    set((store) => {
      selectedItems.forEach((id) => {
        const item = store.items.get(id)
        if (!item || !id) return console.log('Cant find item on blocking')
        ;(item as any).isBlocked = !(item as any).isBlocked
      })
    })
  },
  // TODO: esto probablemente sobra con lo de rafa
  setShouldUpdateCamera: (val: boolean) => {
    set((store) => {
      store.shouldUpdateCamera = val
    })
  },
  // TODO: esto probablemente sobra con lo de rafa

  setCameraPosition(y: number | string) {
    set((store) => {
      store.camera2DPosition.zoom = parseInt(y) / 1000
    })
  },
  setFrameloop: (frameloop: Store['frameloop']) => {
    set((store) => {
      store.frameloop = frameloop
    })
  },
  // TODO: esto probablemente sobra o hay que rehacerlo
  addIncrement: () => {
    set((s) => {
      s.increment = s.increment + 1
    })
  },
  addContadorModificaciones: () => {
    set((s) => {
      s.contadorModificaciones = s.contadorModificaciones + 1
    })
  },
})
